<template>
  <div class="tw-max-h-full">
    <GraphqlDataTable
      ref="graphqlDataTableRef"
      :query="queryWithVariables"
      :max-height-adjustment="30"
      :bulk-actions="bulkActions"
      :order-by="initialOrder"
      v-bind="$attrs"
      :filter-black-list="['accounts']"
      class="eor-employments-table"
    >
      <template v-if="$slots.action" #action="{ row }">
        <slot name="action" :row="row" />
      </template>
    </GraphqlDataTable>

    <WdsDrawer
      v-model:show="partnerWorkerDetailsSidebarVisible"
      position="right"
      dismissible
      width="420px"
      padding="0"
    >
      <PartnerWorkerDetailPanel
        :worker-id="partnerWorkerDetailsProps.workerId"
        :employment-id="partnerWorkerDetailsProps.employmentId"
        @close="closePartnerDetails"
      />
    </WdsDrawer>

    <WdsDrawer
      v-model:show="showContractDetailsPanel"
      position="right"
      width="650px"
      padding="0"
      @update:show="handleDrawerToggle"
    >
      <ContractDetailsPanel
        v-if="showContractDetailsPanel && selectedHireId"
        :hire-id="selectedHireId"
        hide-request-changes-button
        @close="showContractDetailsPanel = false"
      />
    </WdsDrawer>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, reactive, watch } from 'vue'
import { TypedQueryDocumentNode } from 'graphql/index'
import { WdsDrawer } from '@worksome/wds-components'
import { DataTableBulkAction } from '@/components/GraphqlDataTable/types'
import { EmploymentOrderByColumn, SortOrder } from '@/gql/graphql'
import GraphqlDataTable from '@/components/GraphqlDataTable/GraphqlDataTable.vue'
import ContractDetailsPanel from '@/components/ContractDetailsPanel/ContractDetailsPanel.vue'
import PartnerWorkerDetailPanel from '@/components/WorkerDetailPanel/PartnerWorkerDetailPanel.vue'
import {
  addOpenContractDetailPanelListener,
  OpenContractDetailPanelInput,
  addOpenPartnerWorkerDetailPanelListener,
  removeOpenPartnerWorkerDetailPanelListener,
  OpenPartnerWorkerDetailPanelInput,
} from '@/components/GraphqlDataTable/listners'

const props = defineProps<{
  query: TypedQueryDocumentNode
  bulkActions?: DataTableBulkAction[]
  // Employment status filters
  isOnboarded?: boolean
  isActiveTab?: boolean
}>()

const initialOrder = {
  field: EmploymentOrderByColumn.WorkerName,
  order: SortOrder.Asc,
}

// Contract details panel state
const showContractDetailsPanel = ref(false)
const selectedHireId = ref<string | null>(null)

const queryWithVariables = computed(() => props.query)

const handleViewContract = (input: OpenContractDetailPanelInput) => {
  selectedHireId.value = input.hireId
  showContractDetailsPanel.value = true
}

addOpenContractDetailPanelListener(handleViewContract)

// Get reference to the GraphqlDataTable component
const graphqlDataTableRef = ref<InstanceType<typeof GraphqlDataTable> | null>(null)

const handleDrawerToggle = (isOpen: boolean) => {
  // Reset selectedHireId when the drawer closes
  if (!isOpen) {
    selectedHireId.value = null
  }
}

// Partner worker details state and listener
const partnerWorkerDetailsSidebarVisible = ref(false)
const partnerWorkerDetailsProps = reactive<{
  workerId: string
  employmentId?: string
}>({
  workerId: '',
  employmentId: undefined,
})

const closePartnerDetails = () => {
  partnerWorkerDetailsProps.workerId = ''
  partnerWorkerDetailsProps.employmentId = undefined
  partnerWorkerDetailsSidebarVisible.value = false
}

const openPartnerWorkerDetailPanel = (input: OpenPartnerWorkerDetailPanelInput) => {
  partnerWorkerDetailsProps.workerId = input.workerId
  partnerWorkerDetailsProps.employmentId = input.employmentId
  partnerWorkerDetailsSidebarVisible.value = true
}

// Auto-close when tab becomes inactive
watch(
  () => props.isActiveTab,
  (active) => {
    if (active === true) {
      addOpenPartnerWorkerDetailPanelListener(openPartnerWorkerDetailPanel)
    } else {
      removeOpenPartnerWorkerDetailPanelListener(openPartnerWorkerDetailPanel)
      closePartnerDetails()
    }
  },
)

// Initialize listener based on initial tab state
if (props.isActiveTab) {
  addOpenPartnerWorkerDetailPanelListener(openPartnerWorkerDetailPanel)
}
</script>

<style scoped>
/* Fix EoR ActionBar spacing and width */
:deep(.eor-employments-table) {
  .tw--translate-x-1\/2 {
    width: 800px !important;

    .tw-w-\[800px\] {
      width: 100% !important;
      max-width: 100% !important;
      justify-content: space-between !important;
      display: flex !important;

      /* Button styling and dividers */
      > div:last-child {
        gap: 1rem !important; /* Override gap-lg with gap-md equivalent */

        /* Add dividers between buttons */
        > div:not(:first-child)::before {
          content: '';
          position: absolute;
          left: -0.5rem;
          top: 50%;
          transform: translateY(-50%);
          width: 1px;
          height: 1.5rem; /* tw-h-6 */
          background-color: #e5e7eb; /* gray-200 */
        }

        /* Position button containers relatively for divider positioning */
        > div:not(:first-child) {
          position: relative;
        }

        /* Style success-type buttons green (if any are added in future) */
        button[data-test*="mark-example"] {
          color: #1E6021 !important; /* green-600 */

          &:hover {
            color: #047E43 !important; /* green-500 - lighter on hover like other success buttons */
          }
        }
      }
    }
  }
}
</style>
